import React, { useEffect, useMemo, useState } from 'react';
import { ServiceKit } from './ServiceKitStore';
import { Loader, Modal, ModalHandle, MultiSelectGroupEntry, toArray } from 'Mri.Web.Common';
import { Button, Form, FormGroup, Input, ListGroup, ListGroupItem } from 'reactstrap';
import { observer } from 'mobx-react';
import { useStore } from '../hooks/use-store';
import PartDefinitionBase = Mri.Parts.PartDefinitionBase;
import { filter } from 'lodash';
import classNames from 'classnames/bind';
import styles from './ServiceKitModal.module.scss';
import Fuse from 'fuse.js';

const classes = classNames.bind(styles);
interface props {
    serviceKit: ServiceKit | null;
    modalHandle: ModalHandle;
    onClosed(): void;
}
interface Part extends PartDefinitionBase {
    selected?: boolean;
}
export const ServiceKitModal = observer((props: props) => {
    const [serviceKit, setServiceKit] = useState<ServiceKit | null>(null);
    const [serviceKitName, setServiceKitName] = useState('');
    const [serviceKitDescription, setServiceKitDescription] = useState('');
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState([]);
    const [availableParts, setAvailableParts] = useState<Part[]>([]);
    const [filteredParts, setFilteredParts] = useState<Part[]>([]);
    const [selectedParts, setSelectedParts] = useState<string[]>([]);
    const [searchText, setSearchText] = useState('');
    const { partDefinitionsStore, serviceKitStore } = useStore();

    useEffect(() => {
        setServiceKit(
            props.serviceKit
                ? props.serviceKit
                : {
                    name: '',
                    description: '',
                    assemblies: [],
                }
        );
        setServiceKitName(props.serviceKit?.name || '');
        setServiceKitDescription(props.serviceKit?.description || '');
        const initialSelectedParts = props?.serviceKit?.assemblies?.map(part => part.partNumber) || [];
        setSelectedParts(initialSelectedParts);
        partDefinitionsStore
            .loadAllAsync()
            .then(() => {
                const parts = toArray(partDefinitionsStore.partDefinitionBases).map(
                    part => ({ ...part, selected: selectedParts.includes(part.partNumber) } as Part)
                );
                setAvailableParts(parts);
                setFilteredParts(parts);
                filterParts();
            })
            .catch(error => console.error(error))
            .finally(() => setLoading(false));
    }, [props.serviceKit]);

    const submitAsync = () => {
        if (!serviceKit) return;
        const assemblies = availableParts.filter(part => selectedParts.includes(part.partNumber));
        serviceKitStore
            .saveServiceKit({
                ...serviceKit,
                name: serviceKitName,
                description: serviceKitDescription,
                assemblies,
            } as ServiceKit)
            .catch(error => {
                console.error(error);
            })
            .finally(() => {
                setLoading(false);
                setSelectedParts([]);
                props.modalHandle.close();
            });
    };
    const reset = () => {
        setSelectedParts([]);
        setServiceKitName('');
        setServiceKitDescription('');
        setSearchText('');
        filterParts();
        props.onClosed();
    };
    const close = () => {
        reset();
        props.modalHandle.close();
    };

    const filterParts = () => {
        setFilteredParts(
            filter(
                availableParts,
                part => part.partNumber.toLowerCase().includes(searchText.toLowerCase()) && !part.selected
            )
        );
    };

    const togglePartSelection = (part: Part) => {
        const parts = [...availableParts];
        const index = parts.findIndex(p => p.partNumber === part.partNumber);
        parts[index].selected = !parts[index].selected;
        setAvailableParts(parts);
        setSelectedParts(parts.filter(p => p.selected).map(p => p.partNumber));
    };

    const searchParts = (text: string) => {
        setSearchText(text);
        if (!text || text === '') {
            setFilteredParts([...availableParts]);
            return;
        }
        const fuse = new Fuse(availableParts, {
            keys: ['partNumber', 'description'],
            threshold: 0.3,
        });
        const results = fuse.search(text);
        setFilteredParts([...results.map(result => result.item)]);
    };

    const Footer = () => {
        return (
            <div className={classes('footer')}>
                <Button color={'danger'} onClick={close}>
                    Cancel
                </Button>
                <Button color="primary" onClick={submitAsync} disabled={loading}>
                    <Loader loading={loading} showSpinner message="Saving Service Kit..." />
                    {!loading && 'Save Service Kit'}
                </Button>
            </div>
        );
    };

    return (
        <>
            <Modal
                modalHandle={props.modalHandle}
                footer={<Footer />}
                className={classNames('p-0', classes('modal'))}
                size={'md'}
                onClosed={reset}
            >
                {errors.length > 0 && (
                    <div className="alert alert-danger p-2">
                        {errors.map((error, index) => (
                            <div key={index}>{error}</div>
                        ))}
                    </div>
                )}
                <h2>{serviceKit?.serviceKitGuid ? 'Edit Service Kit' : 'Create Service Kit'}</h2>
                <Form>
                    <FormGroup>
                        <label>Name</label>
                        <Input
                            type="text"
                            id="name"
                            value={serviceKitName}
                            onChange={e => setServiceKitName(e.target.value)}
                            required
                        />
                    </FormGroup>
                    <FormGroup>
                        <label>Description</label>
                        <Input
                            type="text"
                            id="description"
                            value={serviceKitDescription}
                            onChange={e => setServiceKitDescription(e.target.value)}
                            required
                        />
                    </FormGroup>
                    {selectedParts.length > 0 && (
                        <h4 className={styles['selected-header']}>Selected Part Assemblies</h4>
                    )}
                    <ListGroup className={classes('selected')} flush>
                        {availableParts.map((part, index) => {
                            if (!selectedParts.includes(part.partNumber)) return;
                            return (
                                <ListGroupItem
                                    key={index}
                                    className={classes('part')}
                                    onClick={() => {
                                        setSelectedParts([...selectedParts.filter(p => p !== part.partNumber)]);
                                    }}
                                >
                                    <span>
                                        {part.partNumber} - {part.description}
                                    </span>
                                    <Button color={'danger'}>-</Button>
                                </ListGroupItem>
                            );
                        })}
                    </ListGroup>
                    <h4>Available Part Assemblies</h4>
                    <FormGroup>
                        <label>Search</label>
                        <Input
                            type="text"
                            id="search"
                            onChange={e => {
                                searchParts(e.target.value);
                            }}
                        />
                    </FormGroup>
                    <ListGroup className={classes('available')} flush>
                        {loading && <ListGroupItem>Loading...</ListGroupItem>}
                        {!loading &&
                            filteredParts.map((part, index) => {
                                if (selectedParts.includes(part.partNumber)) return;
                                return (
                                    <ListGroupItem
                                        key={index}
                                        value={part.partNumber}
                                        className={classes('part')}
                                        onClick={() => {
                                            setSelectedParts([...selectedParts, part.partNumber]);
                                        }}
                                    >
                                        <span>
                                            {part.partNumber} - {part.description}
                                        </span>
                                        <Button color={'primary'}>+</Button>
                                    </ListGroupItem>
                                );
                            })}
                        {loading && <ListGroupItem>Loading...</ListGroupItem>}
                    </ListGroup>
                </Form>
            </Modal>
        </>
    );
});
